import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { RADIUS } from '../radius/radius';
import {
  COLOR_BACKGROUND_PRIMARY,
  COLOR_BORDER_DEFAULT,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SECONDARY,
} from '../colors/colors';

const Container = styled.div`
  width: 100%;
  max-width: 420px;
  box-sizing: border-box;
  margin-top: 16px;
  border-radius: ${RADIUS.MD};
  border: 1px solid ${COLOR_BORDER_DEFAULT};
  background: ${COLOR_BACKGROUND_PRIMARY};
  overflow: hidden;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  gap: 12px;
  border-bottom: ${({ $isLast }) =>
    $isLast ? 'none' : `1px solid ${COLOR_BORDER_DEFAULT}`};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
`;

const ScopeLabel = styled.span`
  font-size: 16px;
  font-weight: 500;
  color: ${COLOR_TEXT_PRIMARY};
  flex: 1;
  min-width: 0;
`;

const Chevron = styled.img`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  transition: transform 0.2s ease;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(90deg)' : 'rotate(0deg)')};
`;

const ExpandPanel = styled.div`
  overflow: hidden;
  max-height: ${({ $expanded }) => ($expanded ? '500px' : '0')};
  transition: max-height 0.3s ease;
  border-bottom: ${({ $expanded, $isLast }) =>
    $expanded && !$isLast ? `1px solid ${COLOR_BORDER_DEFAULT}` : 'none'};
  background: ${COLOR_BACKGROUND_PRIMARY};
`;

const ExpandContent = styled.div`
  padding: 0 16px 16px 16px;
`;

const ScopeValue = styled.div`
  font-size: 16px;
  color: ${COLOR_TEXT_PRIMARY};
  white-space: normal;
  overflow-wrap: anywhere;
`;

const DetailItem = styled.div`
  font-size: 14px;
  color: ${COLOR_TEXT_SECONDARY};
  padding: 4px 0;
`;

const YouDataScopeList = ({ scopes, chevronSrc, onScopeClick }) => {
  const [expandedKey, setExpandedKey] = useState(null);

  const handleRowClick = useCallback(
    (key) => {
      setExpandedKey((prev) => (prev === key ? null : key));
      onScopeClick?.(key);
    },
    [onScopeClick]
  );

  if (!scopes || scopes.length === 0) return null;

  return (
    <Container data-testid="data-scope-list">
      {scopes.map((scope, index) => {
        const isExpanded = expandedKey === scope.key;
        const isLast = index === scopes.length - 1;
        const hasDetails = scope.details && scope.details.length > 0;
        const hasValue = !!scope.value;
        const isExpandable = hasValue || hasDetails;

        return (
          <div key={scope.key}>
            <Row
              onClick={isExpandable ? () => handleRowClick(scope.key) : undefined}
              $clickable={isExpandable}
              $isLast={isLast && !isExpanded}
              data-testid={`scope-row-${scope.key}`}
              aria-expanded={isExpandable ? isExpanded : undefined}
            >
              <ScopeLabel>{scope.label}</ScopeLabel>
              {chevronSrc && (
                <Chevron
                  src={chevronSrc}
                  alt=""
                  $expanded={isExpandable && isExpanded}
                  data-testid={`scope-chevron-${scope.key}`}
                />
              )}
            </Row>

            {isExpandable && (
              <ExpandPanel
                $expanded={isExpanded}
                $isLast={isLast}
                data-testid={`scope-panel-${scope.key}`}
              >
                <ExpandContent>
                  {hasValue && (
                    <ScopeValue data-testid={`scope-value-${scope.key}`}>
                      {scope.value}
                    </ScopeValue>
                  )}
                  {hasDetails &&
                    scope.details.map((detail, i) => (
                      <DetailItem key={i}>{detail}</DetailItem>
                    ))}
                </ExpandContent>
              </ExpandPanel>
            )}
          </div>
        );
      })}
    </Container>
  );
};

YouDataScopeList.propTypes = {
  scopes: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      value: PropTypes.node,
      details: PropTypes.arrayOf(PropTypes.string),
    })
  ),
  chevronSrc: PropTypes.string,
  onScopeClick: PropTypes.func,
};

export { YouDataScopeList };
